"use client";
import React, { useEffect, useRef, useState } from "react";

import { AnnotationsMask, AnnotationsOverlay, AnnotationsOverlayHandle } from "@perceptron/core/annotations-overlay";
import { BoxAnnotation, isBoxAnnotation, isPolygonAnnotation, PolygonAnnotation } from "@perceptron/core/types";
import { calculatePointsBoundingBox, calculateZoomScaleForDimensions } from "@perceptron/utils/zoom";

import { Annotation } from "@demo/components/Annotation";
import { DotField } from "@demo/components/DotField";
import { useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";
import { MediaData } from "@demo/types/base";
import { isZoomBox } from "@demo/utils/annotations";
import { getImageSrc } from "@demo/utils/media";

import { MediaContent } from "./MediaContent";

const calculateBoxZoomScale = (annotation: BoxAnnotation): number => {
  const { top_left, bottom_right } = annotation.coordinates;
  return calculateZoomScaleForDimensions(bottom_right.x - top_left.x, bottom_right.y - top_left.y);
};

const calculatePolygonZoomScale = (annotation: PolygonAnnotation): number => {
  const { width, height } = calculatePointsBoundingBox(annotation.coordinates);
  return calculateZoomScaleForDimensions(width, height);
};

export interface AnnotatedImageViewProps {
  /** The media item to render and annotate. */
  media: MediaData;
  /** Optional per-mode overlay content, rendered inside the (zoom/pan) media area. */
  children?: React.ReactNode;
  /** When true, overlays the perlin-noise {@link DotField} "processing" animation over the image. */
  isProcessing?: boolean;
}

/**
 * Shared annotated-image scaffold: renders {@link MediaContent} inside an
 * {@link AnnotationsOverlay}, draws the visible annotations + dim mask, zooms to a
 * clicked annotation, and clears the selection on a background click. It reads the
 * shared model store ONLY through {@link useAnnotationInteractions}, so it carries
 * no mode/agent state — Compose's `ImageOutput` and Detect's `DetectImageView`
 * both compose it and supply their own outer chrome (height/fullscreen,
 * empty-state) around it.
 */
const AnnotatedImageView = ({ media, children, isProcessing }: AnnotatedImageViewProps) => {
  const {
    visibleAnnotations,
    allFlattenedAnnotations,
    clickedAnnotationId,
    highlightedAnnotationIds,
    visibleAnnotationIds,
    setClickedAnnotationId,
  } = useAnnotationInteractions();

  const annotationsOverlayRef = useRef<AnnotationsOverlayHandle>({
    zoomToElement: () => {},
    resetTransform: () => {},
  });

  // Zoom to a clicked annotation; reset when the selection clears. Uses
  // allFlattenedAnnotations (stable) so hover changes don't re-trigger the zoom.
  useEffect(() => {
    if (clickedAnnotationId !== undefined && annotationsOverlayRef.current) {
      const annotation = allFlattenedAnnotations.find((a) => a.id === clickedAnnotationId);
      if (annotation) {
        const isBox = isBoxAnnotation(annotation);
        const isPolygon = isPolygonAnnotation(annotation);
        // A zoom box's corner brackets animate in over the slower zoom.
        const durationMs = isZoomBox(annotation) ? 500 : 300;
        const elementId = isBox ? `bounding-box-${annotation.id}` : `point-marker-${annotation.id}`;

        // Adaptive scale by annotation type/dimensions.
        const getScale = () => {
          if (isBox) return calculateBoxZoomScale(annotation as BoxAnnotation);
          if (isPolygon) return calculatePolygonZoomScale(annotation as PolygonAnnotation);
          return 1.5; // Fixed scale for points
        };
        const scale = getScale();

        const zoomToAnnotation = () => {
          const element = document.getElementById(elementId);
          if (element && annotationsOverlayRef.current?.zoomToElement) {
            const clickableElement = isBox
              ? element
              : (element.querySelector("polygon, circle, rect") as HTMLElement) || element;

            try {
              annotationsOverlayRef.current.zoomToElement(clickableElement as HTMLElement, scale, durationMs);
            } catch (error) {
              console.error("Error during zoom:", error);
            }
          } else {
            let attempts = 0;
            const tryAgain = () => {
              if (attempts++ < 5 && !document.getElementById(elementId)) {
                requestAnimationFrame(tryAgain);
              }
            };
            requestAnimationFrame(tryAgain);
          }
        };

        requestAnimationFrame(zoomToAnnotation);
      }
    } else if (clickedAnnotationId === undefined && annotationsOverlayRef.current) {
      annotationsOverlayRef.current.resetTransform();
    }
  }, [clickedAnnotationId, allFlattenedAnnotations]);

  const handleMediaClick = (e: React.MouseEvent) => {
    if (clickedAnnotationId !== undefined) {
      e.preventDefault();
      e.stopPropagation();
      setClickedAnnotationId(undefined, undefined);
    }
  };

  // Keep the processing overlay mounted through its exit fade after isProcessing clears.
  const [processingMounted, setProcessingMounted] = useState(!!isProcessing);
  useEffect(() => {
    if (isProcessing) setProcessingMounted(true);
  }, [isProcessing]);

  const processingSrc = getImageSrc(media);

  return (
    <div className="relative h-full w-full">
      <AnnotationsOverlay ref={annotationsOverlayRef}>
        <MediaContent media={media} mediaIndex={0} onMediaClick={handleMediaClick}>
          {visibleAnnotations.map((annotation) => (
            <Annotation annotation={annotation} key={`${annotation.id}-${annotation.type}`} />
          ))}
          <AnnotationsMask
            allFlattenedAnnotations={allFlattenedAnnotations}
            clickedAnnotationId={clickedAnnotationId}
            highlightedAnnotationIds={highlightedAnnotationIds}
            imageUrl={media.url}
            visibleAnnotationIds={visibleAnnotationIds}
          />
          {children}
        </MediaContent>
      </AnnotationsOverlay>
      {processingMounted && processingSrc && (
        <DotField
          imageUrl={processingSrc}
          show={!!isProcessing}
          transitionIn={true}
          transitionOut={true}
          onExited={() => setProcessingMounted(false)}
        />
      )}
    </div>
  );
};

export default AnnotatedImageView;
